import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { waterMeterService } from '../../services/api/water-meter.service';
import { readingService } from '../../services/api/reading.service';
import { Reading } from '../../types/reading';
import _ from 'lodash';

interface ReadingStatsProps {
  userId?: number;
  waterMeterId?: number;
}

interface ChartDataPoint {
  date: string;
  value: number;
}

const ReadingStats: React.FC<ReadingStatsProps> = ({ userId, waterMeterId }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReadings: 0,
    averageConsumption: 0,
    lastReading: 0,
    readingTrend: 'uvecan' as 'uvecan' | 'smanjen' | 'stabilan'
  });
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  useEffect(() => {
    fetchReadingData();
  }, [userId, waterMeterId]);

  const fetchReadingData = async () => {
    try {
      setLoading(true);
      const response = await readingService.getAll({
        water_meter_id: waterMeterId,
        per_page: 1000
      });

      const readings = response.data.data;
      const sorted = _.orderBy(readings, ['reading_date'], ['asc']);
      
      // Calculate stats
      const totalReadings = readings.length;
      const lastReading = sorted[sorted.length - 1]?.reading_value || 0;
      
      let trend: 'uvecan' | 'smanjen' | 'stabilan' = 'stabilan';
      if (sorted.length >= 2) {
        const lastTwo = sorted.slice(-2);
        const diff = lastTwo[1].reading_value - lastTwo[0].reading_value;
        trend = diff > 0 ? 'uvecan' : diff < 0 ? 'smanjen' : 'stabilan';
      }

      let averageConsumption = 0;
      if (sorted.length >= 2) {
        let totalConsumption = 0;
        for (let i = 1; i < sorted.length; i++) {
          totalConsumption += sorted[i].reading_value + sorted[i-1].reading_value;
        }
        averageConsumption = totalConsumption / (sorted.length);
      }

      // Prepare chart data
      const chartData: ChartDataPoint[] = sorted.map(reading => ({
        date: new Date(reading.reading_date).toLocaleDateString(),
        value: reading.reading_value
      }));

      setStats({
        totalReadings,
        averageConsumption,
        lastReading,
        readingTrend: trend
      });
      setChartData(chartData);
    } catch (error) {
      console.error('Error fetching reading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Ukupno očitavanja"
              value={stats.totalReadings}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Prosječna potrošnja"
              value={stats.averageConsumption.toFixed(2)}
              suffix="m³"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Zadnje očitavanje"
              value={stats.lastReading.toFixed(2)}
              suffix="m³"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Trend potrošnje"
              value={stats.readingTrend}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Povijest očitavanja">
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#0284c7" 
                name="Očitavanje (m³)" 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default ReadingStats;